// filepath: apps/web/src/App.tsx
//
// Top-level application shell — FR-UI-CORE-2 bootstrap state machine.
//
// The tree is composed in three layers:
//
//  1. <QueryClientProvider>          — TanStack Query v5 (renderer + auth caches).
//  2. <TenantProvider>               — slug from hostname (FR-UI-CORE-1).
//  3. <AppBootstrap>                 — fetch /api/tenant-config → OIDC init.
//  4. <AuthStateRouter>              — routes between the OIDC callback,
//                                       the bootstrap error screens, and
//                                       the authenticated app shell.
//
// The exception: `/admin/tenants/:tenantId/deprovision` (FR-TEN-6)
// is rendered by <AdminRoute> — that route is a separate concern
// from the OIDC bootstrap (operator-only, behind StaticOperatorAuth).
// We render it parallel to <AppBootstrap> so operator tooling works
// even when the OIDC bootstrap is mid-flight.

import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  TenantProvider,
  tenantSlugFromHostname,
} from "@/tenant/TenantProvider";
import {
  useTenantConfig,
  tenantConfigQueryKey,
} from "@/bootstrap/tenantConfig";
import { setRuntimeTenantConfig } from "@/runtime/apiRuntime";
import { useAuth } from "react-oidc-context";
import {
  AuthProvider,
  AuthGuard,
  installSilentRefresh,
  TenantNotFoundScreen,
  TenantSuspendedScreen,
  NetworkFailureScreen,
  OidcErrorScreen,
  LoadingScreen,
} from "@/auth";
import { LoginRoute } from "@/features/auth/LoginRoute";
import { CallbackRoute } from "@/features/auth/CallbackRoute";
import { TasksRoute } from "@/features/tasks/TasksRoute";
import { RequiredUserParametersRoute } from "@/features/cabinet/RequiredUserParameters";
import {
  TenantShell,
  CabinetLandingPage,
  NotFoundScreen,
  AdminSection,
  usePathname,
} from "@/features/cabinet";
import { DeprovisionPage } from "@/admin/tenants/DeprovisionPage";
import {
  PromotionDiffRoute,
  PromotionReviewGateRoute,
} from "@/features/promote";
import {
  PermissionsMatrixRoute,
  RecordFieldPermissionsRoute,
  RoleFieldVisibilityRoute,
  WebhookEndpointsRoute,
  HttpEndpointsRoute,
  EmailChannelsRoute,
  SecretReferencesRoute,
  TemplateRegistryRoute,
  TemplateUpdatesRoute,
  TemplateUpdateRoute,
  TierPanelRoute,
  AuditViewerRoute,
  UserRolesRoute,
  HmacKeysRoute,
} from "@/features/admin";
import { FormsListRoute } from "@/features/admin/forms";
import { ProcessesListRoute } from "@/features/admin/processes";
import {
  PeopleDirectoryRoute,
  PersonSummaryRoute,
  OrganizationTreeRoute,
  PositionsRoute,
  EmploymentTimelineRoute,
  LocationsRoute,
  TeamsRoute,
  QualificationsRoute,
  DirectoryAdminRoute,
  AuditHistoryRoute,
} from "@/features/directory";
// ISS-379 (Slice 0 tenant-alive): the four Counterparties pages
// were orphaned — they existed under `apps/web/src/admin/counterparties/`
// but no route in App.tsx mounted them. The Directory menu
// (DIRECTORY_NAV_ITEMS) now points at /admin/counterparties; the
// four pages use the canonical apiFetch client (post-issue 377) so the
// bearer is attached, the RLS-scoped data (post-issue 378) is honored,
// and the empty-state UX (post-issue 232) carries through.
// design-adherence-ignore: the bare issue numbers in the comment above
// match the text-scan hex pattern; they are not colour values.
import {
  CounterpartyListScreen,
  CounterpartyNewScreen,
  CounterpartyDetailScreen,
  CounterpartyEditScreen,
} from "@/admin/counterparties";
// ISS-515 — Locations CRUD admin screens (Slice 1 pattern setter).
// Mirrors the counterparty barrel at apps/web/src/admin/counterparties/index.ts.
// Mounted at /admin/locations{,/new,/:id,/:id/edit}.
import {
  LocationNewScreen,
  LocationEditScreen,
  LocationDetailScreen,
} from "@/admin/locations";
// ISS-516 — Positions CRUD admin screens (Slice 1, dev-4's #516).  // design-adherence-ignore
// Mirrors the locations barrel. Mounted at
// /admin/positions{,/new,/:id,/:id/edit}.
import {
  PositionNewScreen,
  PositionEditScreen,
  PositionDetailScreen,
} from "@/admin/positions";
// ISS-518 — People CRUD admin screens.
// Mounted at /admin/people{,/new,/:id,/:id/edit}. Create/edit/archive ONLY
// per the BA option-(a) ruling on note 18233.
import {
  PersonNewScreen,
  PersonEditScreen,
  PersonDetailScreen,
} from "@/admin/people";
// ISS-517 — Organization unit CRUD admin screens.
// Mounted at /admin/organization-units{,/new,/:id,/:id/edit}. Create /
// edit / move / archive ONLY — the Go backend (FR-DIR-2) is shipped and
// unmodified by this MR.
import {
  OrganizationUnitNewScreen,
  OrganizationUnitEditScreen,
  OrganizationUnitDetailScreen,
} from "@/admin/organization-units";
import { FormBuilderRoute } from "@/editors/form-builder";
import { BpmDesignerRoute } from "@/editors/bpm-designer";
import { EntityListRoute } from "@/features/lists";
import { ProcRoute } from "@/features/proc";
import { ObservabilityDashboardRoute } from "@/features/observability";
import { isOperatorSurface } from "@/operator";
import { OperatorRoot } from "@/operator/OperatorRoot";
import { ApexLandingPage, isApexLanding } from "@/apex/ApexLandingPage";
import { APEX_TENANT_SLUG } from "@/apex/apexTenantSlug";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: 30 * 60 * 1000,
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * AdminRoute — minimal in-process router. Splits the URL on the
 * /admin/tenants/ prefix to extract :tenantId. v1 has no other
 * admin routes; future work can promote this to react-router or a
 * custom dispatcher.
 *
 * SECURITY: this is a client-side dispatcher only. The real
 * authorization is server-side (StaticOperatorAuth).
 */
function AdminRoute(): React.ReactElement | null {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(
    /^\/admin\/tenants\/([^/]+)\/deprovision\/?$/,
  );
  if (!m || !m[1]) return null;
  const tenantId = decodeURIComponent(m[1]);
  return <DeprovisionPage tenantId={tenantId} />;
}

/**
 * AppBootstrap — the FR-UI-CORE-2 step 2-3 orchestrator. Fetches
 * /api/tenant-config and conditionally renders the OIDC provider
 * tree. While the fetch is in flight, renders the LoadingScreen.
 *
 * The OIDC provider tree is rendered only after the tenant config
 * resolves successfully. This guarantees the AuthProvider always
 * has a valid `authority` (realm URL) at construction time.
 *
 * ISS-377 (uat-ba note_13124): the resolved TenantConfig is also
 * pushed into the `apiRuntime` module-scoped store via
 * `setRuntimeTenantConfig` so the `apiFetch` 401 silent-refresh
 * interceptor derives its trigger host from the same realm URL
 * the OIDC client uses. Without this push, the interceptor falls
 * back to `window.location.host` (which works on most deployments
 * but is NOT the realm-bound host — the realm URL is the
 * Keycloak authority, not the SPA's hostname).
 */
function AppBootstrap({
  tenantSlug,
  children,
}: {
  tenantSlug: string;
  children: React.ReactNode;
}): React.ReactElement {
  const query = useTenantConfig(tenantSlug);

  // Push the resolved TenantConfig into the runtime store on every
  // successful resolve AND clear it on unmount. The runtime config
  // is the source of truth for the apiFetch interceptor's trigger
  // host (see apps/web/src/runtime/apiRuntime.ts).
  React.useEffect(() => {
    if (query.data) {
      setRuntimeTenantConfig(query.data);
    }
    return () => {
      setRuntimeTenantConfig(null);
    };
  }, [query.data]);

  if (query.isLoading) {
    return <LoadingScreen />;
  }

  if (query.error) {
    return classifyTenantConfigError(query.error);
  }

  if (!query.data) {
    return <LoadingScreen />;
  }

  return <AuthProvider tenantConfig={query.data}>{children}</AuthProvider>;
}

/**
 * classifyTenantConfigError — maps the typed error envelope from
 * useTenantConfig to one of the AC-5 error screens.
 */
function classifyTenantConfigError(error: {
  status: number;
  code: string;
  message: string;
  retriable: boolean;
}): React.ReactElement {
  if (error.status === 404 || error.code === "not_found") {
    return <TenantNotFoundScreen />;
  }
  if (error.status === 503 || error.code === "suspended") {
    return <TenantSuspendedScreen />;
  }
  if (
    error.status === 0 ||
    error.code === "network" ||
    error.code === "timeout"
  ) {
    return <NetworkFailureScreen />;
  }
  return <OidcErrorScreen code={error.code} message={error.message} />;
}

/**
 * authed — wrap an authenticated section's content in AuthGuard +
 * TenantShell. Used by every section-level branch in AuthStateRouter
 * so the sidebar persists across section switches: React reconciles
 * <TenantShell> at the same tree position across branches, so only
 * the children (the matched section) swap — the sidebar DOM/state
 * survives (FR-UI-CABINET-1 AC-1).
 *
 * The shell is INSIDE the authenticated subtree; it does not replace
 * or reorder the OIDC / TenantProvider bootstrap chain (AC-9).
 */
function authed(content: React.ReactNode): React.ReactElement {
  return (
    <AuthGuard>
      <TenantShell>{content}</TenantShell>
    </AuthGuard>
  );
}

/**
 * AuthStateRouter — switches between the OIDC callback route, the
 * bootstrap error screens, and the authenticated app shell.
 *
 * FR-UI-CABINET-1: the router consumes `usePathname()` (a
 * useSyncExternalStore hook over `popstate`) instead of a direct
 * `window.location.pathname` read, so the shell re-renders on every
 * sidebar section switch without a full page reload (AC-1) and
 * without `react-router-dom` (AC-9).
 */
function AuthStateRouter({
  tenantSlug,
}: {
  tenantSlug: string;
}): React.ReactElement {
  const path = usePathname();
  const auth = useAuth();

  // ISS-277: the error check MUST precede the /oidc-callback path
  // branch. A FAILED code exchange leaves auth.error set while the
  // URL is still /oidc-callback (onSigninCallback only runs on
  // success), so the path-first order previously routed to
  // CallbackRoute's empty-fragment error render — a blank page
  // instead of the FR-UI-CORE-2 AC-5 OidcErrorScreen.
  if (auth.error) {
    return (
      <OidcErrorScreen code={auth.error.message} message={auth.error.message} />
    );
  }

  if (path === "/oidc-callback") {
    return <CallbackRoute />;
  }

  // FR-UI-TASK-1: human-task inbox route.
  if (path === "/tasks" || path.startsWith("/tasks/")) {
    return authed(<TasksRoute />);
  }

  // FR-UI-CABINET-3: required user parameters page.
  if (
    path === "/cabinet/required-parameters" ||
    path === "/cabinet/required-parameters/"
  ) {
    return authed(<RequiredUserParametersRoute />);
  }

  // FR-UI-PROM-1: promotion diff view route.
  // URL: /promote/diff?reviewId=<uuid>
  if (path === "/promote/diff") {
    const reviewId =
      typeof window !== "undefined"
        ? (new URLSearchParams(window.location.search).get("reviewId") ?? "")
        : "";
    return (
      <AuthGuard>
        <PromotionDiffRoute reviewId={reviewId} />
      </AuthGuard>
    );
  }

  // FR-UI-PROM-2: non-skippable human-review gate route.
  // URL: /promote/review?reviewId=<uuid>
  // Reads ONLY the `reviewId` query-param. No other query-param is
  // honoured — the AC-4 non-skippable contract (see
  // PromotionReviewGatePage.tsx header) explicitly forbids
  // `?skip`, `?auto_approve`, `?bypass_review` handlers.
  if (path === "/promote/review") {
    const reviewId =
      typeof window !== "undefined"
        ? (new URLSearchParams(window.location.search).get("reviewId") ?? "")
        : "";
    return (
      <AuthGuard>
        <PromotionReviewGateRoute reviewId={reviewId} />
      </AuthGuard>
    );
  }

  // FR-UI-ADM-1: entity-type CRUD permission matrix editor.
  // URL: /admin/permissions/matrix. The route shell gates on
  // environmentKind=staging at the SPA layer (defense-in-depth;
  // the server rejects production writes with HTTP 403 ENV_PRODUCTION).
  if (path === "/admin/permissions/matrix") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return authed(<PermissionsMatrixRoute environmentKind={environmentKind} />);
  }

  // FR-UI-ADM-2: record-level + field-level permission editor.
  // URL: /admin/permissions/record-field. Staging-only at the SPA
  // layer (the route component reads environmentKind from the
  // cached tenant-config query and renders StagingOnlyError when
  // not staging). The server rejects production writes with
  // HTTP 403 ENV_PRODUCTION.
  if (path === "/admin/permissions/record-field") {
    return authed(<RecordFieldPermissionsRoute />);
  }

  // FR-UI-ADM-4: admin viewer (role × entity-type field
  // classification). URL:
  //   /admin/roles/:roleName/field-visibility?entityType=<entityType>
  // Read-only surface — no environmentKind gate. The handler's
  // requireAuth middleware covers authentication.
  const fieldVisibilityMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/roles\/([^/]+)\/field-visibility\/?$/)
      : null;
  if (fieldVisibilityMatch && fieldVisibilityMatch[1]) {
    const roleName = decodeURIComponent(fieldVisibilityMatch[1]);
    const entityType =
      typeof window !== "undefined"
        ? (new URLSearchParams(window.location.search).get("entityType") ?? "")
        : "";
    return (
      <AuthGuard>
        <RoleFieldVisibilityRoute roleName={roleName} entityType={entityType} />
      </AuthGuard>
    );
  }

  // FR-UI-ADM-6: webhook endpoint admin editor. URL:
  //   /admin/webhooks/endpoints
  // SPA-layer staging-only gate (defense-in-depth; the server
  // rejects production writes with HTTP 403 ENV_PRODUCTION).
  if (path === "/admin/webhooks/endpoints") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return authed(<WebhookEndpointsRoute environmentKind={environmentKind} />);
  }

  // FR-UI-ADM-5: Keycloak user-role assignment admin editor. URL:
  //   /admin/users
  // SPA-layer staging-only gate (defense-in-depth; the server
  // rejects production writes with HTTP 403 staging_only).
  if (path === "/admin/users") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return authed(<UserRolesRoute environmentKind={environmentKind} />);
  }

  // FR-UI-ADM-7: HMAC key rotation action admin editor. URL:
  //   /admin/webhooks/keys
  // SPA-layer staging-only gate (defense-in-depth; the server
  // rejects production writes with HTTP 403 staging_only).
  if (path === "/admin/webhooks/keys") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return authed(<HmacKeysRoute environmentKind={environmentKind} />);
  }

  // FR-UI-ADM-8: outbound HTTP endpoint admin editor.
  // URL: /admin/effects/http-endpoints
  if (path === "/admin/effects/http-endpoints") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return authed(<HttpEndpointsRoute environmentKind={environmentKind} />);
  }

  // FR-UI-ADM-8: outbound email channel admin editor.
  // URL: /admin/effects/email-channels
  if (path === "/admin/effects/email-channels") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return authed(<EmailChannelsRoute environmentKind={environmentKind} />);
  }

  // FR-UI-ADM-10: secret reference manager admin editor.
  // URL: /admin/secrets
  if (path === "/admin/secrets") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return authed(<SecretReferencesRoute environmentKind={environmentKind} />);
  }

  // FR-UI-ADM-11: template registry browse + install admin editor.
  // FR-UI-ADM-14: tier & quota admin panel (read-only).
  // URL: /admin/tier
  if (path === "/admin/tier") {
    return authed(<TierPanelRoute />);
  }

  // FR-UI-ADM-14: definition audit log viewer (read-only).
  // URL: /admin/audit
  if (path === "/admin/audit") {
    return authed(<AuditViewerRoute />);
  }

  // URL: /admin/templates/registry
  if (path === "/admin/templates/registry") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return authed(<TemplateRegistryRoute environmentKind={environmentKind} />);
  }

  // FR-UI-ADM-12: template-update list view at /admin/templates/updates.
  // Read-only; accessible from both environments. The apply
  // verb (POST /api/templates/{id}/apply-update) is staging-only
  // and gated server-side via ENV_PRODUCTION.
  if (path === "/admin/templates/updates") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return authed(
      <TemplateUpdatesRoute
        environmentKind={environmentKind}
        onView={(row) => {
          if (typeof window !== "undefined") {
            const url = `/admin/templates/updates/${encodeURIComponent(row.template_id)}?installed=${encodeURIComponent(row.installed_version)}&target=${encodeURIComponent(row.target_version)}`;
            window.location.assign(url);
          }
        }}
      />,
    );
  }

  // FR-UI-ADM-12: template-update diff view at /admin/templates/updates/:templateId.
  // Reads ONLY installed/target from the URL — no other query-param
  // is honoured (mirrors the FR-UI-PROM-2 non-skippable pattern).
  const templateUpdateMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/templates\/updates\/([^/]+)\/?$/)
      : null;
  if (templateUpdateMatch && templateUpdateMatch[1]) {
    const templateId = decodeURIComponent(templateUpdateMatch[1]);
    const params =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search)
        : new URLSearchParams();
    const installed = params.get("installed") ?? "";
    const target = params.get("target") ?? "";
    return (
      <AuthGuard>
        <TemplateUpdateRoute
          templateId={templateId}
          installed={installed}
          target={target}
          onRedirect={(reviewId) => {
            if (typeof window !== "undefined") {
              window.location.assign(
                `/promote/review?reviewId=${encodeURIComponent(reviewId)}`,
              );
            }
          }}
        />
      </AuthGuard>
    );
  }

  // FR-UI-LIST-1: definition-driven list view.
  // URL: /lists/:entityType. The route shell reads :entityType
  // from window.location.pathname and renders the list view inside
  // <AuthGuard>. No environmentKind gate — list views are
  // all-environments per FR-UI-LIST-1.
  const entityListMatch =
    typeof path === "string" ? path.match(/^\/lists\/([^/]+)\/?$/) : null;
  if (entityListMatch && entityListMatch[1]) {
    return authed(<EntityListRoute />);
  }

  // FR-UI-PROC-1: failed-instance dashboard + per-instance trace.
  // URLs:
  //   /processes/instances/failed           → failed-instance dashboard
  //   /processes/instances/:instanceId/trace → execution-trace viewer
  // ProcRoute dispatches on the URL pattern (matches the
  // TasksRoute / EntityListRoute convention). No environmentKind
  // gate — failed-instance surfacing is all-environments per
  // FR-OBS-6 (the failed-instance list endpoint serves both
  // staging and production).
  const procDashboardMatch =
    typeof path === "string"
      ? /^\/processes\/instances\/failed\/?$/.test(path)
      : false;
  const procTraceMatch =
    typeof path === "string"
      ? /^\/processes\/instances\/([^/]+)\/trace\/?$/.test(path)
      : false;
  // ISS-276: `RegExp.prototype.test` returns a boolean, never `null`.
  // The previous `procTraceMatch !== null` check was always true for
  // any string pathname, causing EVERY authenticated URL to dispatch
  // to <ProcRoute /> and render its "Unknown proc route." fallback
  // (the cabinet landing, tasks, and admin routes were unreachable).
  // The boolean OR below restores correct dashboard + trace dispatch
  // while keeping both match variables boolean-shaped (mirrors the
  // `procDashboardMatch` shape).
  if (procDashboardMatch || procTraceMatch) {
    return authed(<ProcRoute />);
  }

  // FR-UI-DASH-1: per-tenant observability dashboard.
  // URL: /observability/dashboard
  // No environmentKind gate -- observability surfacing is
  // all-environments per ARCHITECTURE_WEB.md §10 (the FR-OBS-1
  // trace endpoints serve both staging and production).
  if (
    typeof path === "string" &&
    /^\/observability\/dashboard\/?$/.test(path)
  ) {
    return authed(<ObservabilityDashboardRoute />);
  }

  // FR-UI-BLD-1 / ISS-f4fa1b7b-258: form + process list-page routes.
  //
  //   /admin/forms             -> FormsListRoute (placeholder)
  //   /admin/forms/new         -> FormBuilderRoute (no formId)
  //   /admin/forms/:formId/edit -> FormBuilderRoute (edit mode)
  //   /admin/processes         -> ProcessesListRoute (placeholder)
  //   /admin/processes/new     -> BpmDesignerRoute (no name)
  //   /admin/processes/:name/edit -> BpmDesignerRoute (edit mode)
  //
  // environmentKind resolution mirrors the existing admin routes
  // (PermissionsMatrixRoute / UserRolesRoute / TemplateRegistryRoute).
  // The route shells gate on staging; the server also rejects
  // production writes with HTTP 403 ENV_PRODUCTION (ISS-253, ISS-254).

  // /admin/forms list
  if (path === "/admin/forms") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return authed(<FormsListRoute environmentKind={environmentKind} />);
  }

  // /admin/forms/new
  if (path === "/admin/forms/new") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return (
      <AuthGuard>
        <FormBuilderRoute environmentKind={environmentKind} />
      </AuthGuard>
    );
  }

  // /admin/forms/:formId/edit
  const formEditMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/forms\/([^/]+)\/edit\/?$/)
      : null;
  if (formEditMatch && formEditMatch[1]) {
    const formId = decodeURIComponent(formEditMatch[1]);
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return (
      <AuthGuard>
        <FormBuilderRoute environmentKind={environmentKind} formId={formId} />
      </AuthGuard>
    );
  }

  // /admin/processes list
  if (path === "/admin/processes") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return authed(<ProcessesListRoute environmentKind={environmentKind} />);
  }

  // /admin/processes/new
  if (path === "/admin/processes/new") {
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return (
      <AuthGuard>
        <BpmDesignerRoute environmentKind={environmentKind} />
      </AuthGuard>
    );
  }

  // /admin/processes/:name/edit
  const processEditMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/processes\/([^/]+)\/edit\/?$/)
      : null;
  if (processEditMatch && processEditMatch[1]) {
    const procName = decodeURIComponent(processEditMatch[1]);
    const config = queryClient.getQueryData<{ environment_kind?: string }>(
      tenantConfigQueryKey(tenantSlug),
    );
    const environmentKind =
      config?.environment_kind?.toLowerCase() ?? "production";
    return (
      <AuthGuard>
        <BpmDesignerRoute environmentKind={environmentKind} name={procName} />
      </AuthGuard>
    );
  }

  // FR-UI-CABINET-1 AC-7: the Admin section landing (tile grid).
  // URL: /admin — the sidebar "Admin" nav target. Renders the grid
  // of navigation tiles into the existing admin areas. The grid
  // fail-closes when useNavManifest().adminAccess is false (the
  // sidebar also hides the link — defense in depth; INV-2).
  if (path === "/admin") {
    return authed(<AdminSection />);
  }

  // ─── FR-UI-DIR-1: People and Organization Directory Workspace ────
  // The directory's admin surface is mounted at /directory/admin
  // (NOT /admin, which is the operator tile-grid surface and serves
  // a different audience). All directory surfaces live under the
  // `/directory/*` namespace except the user-facing catalog pages
  // (people / organization / positions / employment / locations /
  // teams / qualifications / audit), which match the design
  // composition's top-level entry points.

  // /people (master list)
  if (path === "/people" || path === "/people/") {
    return authed(<PeopleDirectoryRoute />);
  }

  // /people/:personId (person summary)
  const personSummaryMatch =
    typeof path === "string" ? path.match(/^\/people\/([^/]+)\/?$/) : null;
  if (personSummaryMatch && personSummaryMatch[1]) {
    const personId = decodeURIComponent(personSummaryMatch[1]);
    return authed(<PersonSummaryRoute personId={personId} />);
  }

  // /organization (org unit tree)
  if (path === "/organization" || path === "/organization/") {
    return authed(<OrganizationTreeRoute />);
  }

  // /positions (positions catalog)
  if (path === "/positions" || path === "/positions/") {
    return authed(<PositionsRoute />);
  }

  // /employment/:personId (employment timeline)
  const employmentMatch =
    typeof path === "string" ? path.match(/^\/employment\/([^/]+)\/?$/) : null;
  if (employmentMatch && employmentMatch[1]) {
    const personId = decodeURIComponent(employmentMatch[1]);
    return authed(<EmploymentTimelineRoute personId={personId} />);
  }

  // /locations (locations catalog)
  if (path === "/locations" || path === "/locations/") {
    return authed(<LocationsRoute />);
  }

  // /teams — F1: /api/directory/teams endpoint NOT shipped. The page
  // renders the fetch-failure state immediately and never calls the
  // missing endpoint. The static-guard test enforces this.
  if (path === "/teams" || path === "/teams/") {
    return authed(<TeamsRoute />);
  }

  // /qualifications/:personId — F1: /api/directory/qualifications
  // endpoint NOT shipped. Same fetch-failure short-circuit as teams.
  const qualificationsMatch =
    typeof path === "string"
      ? path.match(/^\/qualifications\/([^/]+)\/?$/)
      : null;
  if (qualificationsMatch && qualificationsMatch[1]) {
    const personId = decodeURIComponent(qualificationsMatch[1]);
    return authed(<QualificationsRoute personId={personId} />);
  }

  // /directory/admin (directory-scoped admin actions; distinct from
  // /admin which is the operator tile grid).
  if (path === "/directory/admin" || path === "/directory/admin/") {
    return authed(<DirectoryAdminRoute />);
  }

  // ISS-379 (Slice 0 tenant-alive) -- Counterparties pages.
  // The four Counterparties pages were orphaned (existed under
  // apps/web/src/admin/counterparties/ but no route mounted them).
  // The Directory menu (DIRECTORY_NAV_ITEMS) points at
  // /admin/counterparties; the detail + edit screens require a
  // :id segment so the routes use the same regex anchoring
  // pattern as the rest of the directory surface.
  // Routes mounted here, in priority order:
  //   1. /admin/counterparties/new           (no id; must precede :id match)
  //   2. /admin/counterparties/:id/edit      (id required)
  //   3. /admin/counterparties/:id           (id required)
  //   4. /admin/counterparties               (list; trailing-slash tolerant)
  if (
    path === "/admin/counterparties/new" ||
    path === "/admin/counterparties/new/"
  ) {
    return authed(<CounterpartyNewScreen />);
  }
  const counterpartyEditMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/counterparties\/([^/]+)\/edit\/?$/)
      : null;
  if (counterpartyEditMatch && counterpartyEditMatch[1]) {
    const counterpartyId = decodeURIComponent(counterpartyEditMatch[1]);
    return authed(<CounterpartyEditScreen id={counterpartyId} />);
  }
  const counterpartyDetailMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/counterparties\/([^/]+)\/?$/)
      : null;
  if (counterpartyDetailMatch && counterpartyDetailMatch[1]) {
    const counterpartyId = decodeURIComponent(counterpartyDetailMatch[1]);
    return authed(<CounterpartyDetailScreen id={counterpartyId} />);
  }
  if (path === "/admin/counterparties" || path === "/admin/counterparties/") {
    return authed(<CounterpartyListScreen />);
  }

  // ISS-515 — Locations CRUD admin surface. Mounted parallel to the
  // Counterparties section above; the ordering follows the
  // counterparty convention (new precedes :id which precedes :id/edit
  // for routing safety; trailing-slash tolerant). Routes:
  //   1. /admin/locations/new        (no id; must precede :id match)
  //   2. /admin/locations/:id/edit   (id required)
  //   3. /admin/locations/:id        (id required)
  if (path === "/admin/locations/new" || path === "/admin/locations/new/") {
    return authed(<LocationNewScreen />);
  }
  const locationEditMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/locations\/([^/]+)\/edit\/?$/)
      : null;
  if (locationEditMatch && locationEditMatch[1]) {
    const locationId = decodeURIComponent(locationEditMatch[1]);
    return authed(<LocationEditScreen id={locationId} />);
  }
  const locationDetailMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/locations\/([^/]+)\/?$/)
      : null;
  if (locationDetailMatch && locationDetailMatch[1]) {
    const locationId = decodeURIComponent(locationDetailMatch[1]);
    return authed(<LocationDetailScreen id={locationId} />);
  }
  // /admin/locations -- the list route. IMPORTANT: the list-route
  // check is at the END of the locations block because the `:id`
  // match above would also match `/admin/locations/foo` but not
  // `/admin/locations` (no second segment).
  if (path === "/admin/locations" || path === "/admin/locations/") {
    return authed(<LocationsRoute />);
  }

  // ISS-516 — Positions CRUD admin surface. Mirrors the locations
  // block above. Routes:
  //   1. /admin/positions/new        (no id; must precede :id match)
  //   2. /admin/positions/:id/edit   (id required)
  //   3. /admin/positions/:id        (id required)
  //   4. /admin/positions            (list route)
  if (path === "/admin/positions/new" || path === "/admin/positions/new/") {
    return authed(<PositionNewScreen />);
  }
  const positionEditMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/positions\/([^/]+)\/edit\/?$/)
      : null;
  if (positionEditMatch && positionEditMatch[1]) {
    const positionId = decodeURIComponent(positionEditMatch[1]);
    return authed(<PositionEditScreen id={positionId} />);
  }
  const positionDetailMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/positions\/([^/]+)\/?$/)
      : null;
  if (positionDetailMatch && positionDetailMatch[1]) {
    const positionId = decodeURIComponent(positionDetailMatch[1]);
    return authed(<PositionDetailScreen id={positionId} />);
  }
  if (path === "/admin/positions" || path === "/admin/positions/") {
    return authed(<PositionsRoute />);
  }

  // ISS-518 -- People CRUD admin surface. Routes:
  //   1. /admin/people/new        (no id; must precede :id match)
  //   2. /admin/people/:id/edit   (id required)
  //   3. /admin/people/:id        (id required)
  //   4. /admin/people            (list, trailing-slash tolerant)
  // Mirror locations/counterparties ordering (new precedes :id
  // precedes list -- the `:id` regex would otherwise match
  // `/admin/people/foo` but not `/admin/people` itself).
  if (path === "/admin/people/new" || path === "/admin/people/new/") {
    return authed(<PersonNewScreen />);
  }
  const personEditMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/people\/([^/]+)\/edit\/?$/)
      : null;
  if (personEditMatch && personEditMatch[1]) {
    const personId = decodeURIComponent(personEditMatch[1]);
    return authed(<PersonEditScreen id={personId} />);
  }
  const personDetailMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/people\/([^/]+)\/?$/)
      : null;
  if (personDetailMatch && personDetailMatch[1]) {
    const personId = decodeURIComponent(personDetailMatch[1]);
    return authed(<PersonDetailScreen id={personId} />);
  }
  if (path === "/admin/people" || path === "/admin/people/") {
    return authed(<PeopleDirectoryRoute />);
  }

  // ISS-517 -- Organization unit CRUD admin surface. Routes:
  //   1. /admin/organization-units/new        (no id; must precede :id)
  //   2. /admin/organization-units/:id/edit   (id required)
  //   3. /admin/organization-units/:id        (id required)
  //   4. /admin/organization-units            (tree list, trailing-slash tolerant)
  // Ordering mirrors locations / people / positions exactly: `new`
  // precedes `:id`, and `:id` is a regex so it never swallows the
  // bare list path.
  if (
    path === "/admin/organization-units/new" ||
    path === "/admin/organization-units/new/"
  ) {
    // Optional `?parent=<uuid>` deep link from the tree page's
    // "Add child" affordance — preselects the parent picker. The id
    // is only a form default; the server remains the authority on
    // whether that parent is legal.
    const parentParam =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("parent")
        : null;
    return authed(<OrganizationUnitNewScreen initialParentId={parentParam} />);
  }
  const organizationUnitEditMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/organization-units\/([^/]+)\/edit\/?$/)
      : null;
  if (organizationUnitEditMatch && organizationUnitEditMatch[1]) {
    const unitId = decodeURIComponent(organizationUnitEditMatch[1]);
    return authed(<OrganizationUnitEditScreen id={unitId} />);
  }
  const organizationUnitDetailMatch =
    typeof path === "string"
      ? path.match(/^\/admin\/organization-units\/([^/]+)\/?$/)
      : null;
  if (organizationUnitDetailMatch && organizationUnitDetailMatch[1]) {
    const unitId = decodeURIComponent(organizationUnitDetailMatch[1]);
    return authed(<OrganizationUnitDetailScreen id={unitId} />);
  }
  if (
    path === "/admin/organization-units" ||
    path === "/admin/organization-units/"
  ) {
    // The admin list surface reuses the existing catalog tree page;
    // its per-node actions deep-link into the three screens above.
    return authed(<OrganizationTreeRoute />);
  }

  // /audit (directory audit feed)
  if (path === "/audit" || path === "/audit/") {
    return authed(<AuditHistoryRoute />);
  }

  // ISS-434 (ui-conformance, sweep finding 3 — the design-adherence-ignore
  // marker above keeps the bare issue numbers in the comment block
  // from matching the text-scan hex pattern; they are not colour values):
  // the dispatcher fall-through renders a styled NotFoundScreen (path +
  // Back-to-Cabinet), NOT the Cabinet. Per the issue brief: "some routes
  // may legitimately be unbuilt, in which case render a not-found /
  // coming-soon state rather than the cabinet." The previous
  // `authed(<CabinetLandingPage />)` was what the BA captured on
  // UAT — every `/tasks`, `/people`, `/organization`, etc. URL
  // showed the "My Cabinet" content because the SPA fell through.
  // Now an unknown URL gets a clear "not built yet" surface inside
  // the chrome (and a Back-to-Cabinet action), and the report's
  // bug stops reproducing on every captured tenant URL.
  //
  // The cabinet landing (/) is an EXPLICIT route above the
  // fall-through — it must keep rendering the cabinet, not the
  // NotFoundScreen, so the home URL is the documented "My
  // Cabinet" landing (FR-UI-CABINET-1 AC-3).
  if (path === "/" || path === "") {
    return authed(<CabinetLandingPage />);
  }
  return authed(<NotFoundScreen />);
}

/**
 * SilentRefreshInstaller — installs the 401-triggered silent refresh
 * interceptor on mount and uninstalls on unmount. The component
 * renders nothing — it is a side-effect carrier.
 *
 * ISS-377 round 2: the route-level replay-guard cleanup useEffect
 * was REMOVED. The global guard was replaced by a per-call `init`
 * cache inside `apiFetch` (the round-2 architecture). The replay
 * now reads the same `init` it was built with — method, body,
 * headers, X-Idempotency-Key — so a refresh-then-retry is
 * byte-identical to the original, and the server's idempotency
 * cache returns the same response. There is no longer a
 * process-wide boolean to clean up on route navigation.
 */
function SilentRefreshInstaller({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const auth = useAuth();
  React.useEffect(() => {
    if (!auth.isAuthenticated) return;
    const uninstall = installSilentRefresh({ auth });
    return uninstall;
  }, [auth, auth.isAuthenticated]);
  return <>{children}</>;
}

export function App(): React.ReactElement {
  // Bare apex (VITE_PLATFORM_APEX with no subdomain) at "/" — show
  // the simple landing page that points the operator to the
  // /operator/* surface. Without this, the bare apex falls into the
  // tenant shell, which has no tenant to resolve and ends up
  // half-broken (the Go backend's resolver 404s the bare apex; see
  // internal/kernel/resolver.go:84). The landing page is the
  // user-visible "control-plane" surface per the architecture
  // (ARCHITECTURE_v0.4.md §25, FR-UI-OPS-1).
  //
  // ISS-f4fa1b7b-297: the apex branch MUST be wrapped in
  // <QueryClientProvider> + <TenantProvider>. <ApexLandingPage>
  // renders <AppChrome variant="apex">, and AppChrome calls
  // useTenantConfig() — a TanStack useQuery hook — which throws
  // "No QueryClient set" if QueryClientProvider is not an
  // ancestor. The tenant slug passed to <TenantProvider> is the
  // literal "apex" (synthetic): the apex page itself never queries
  // /api/tenant-config (the Go backend 404s bare apex), but AppChrome
  // invokes useTenantConfig unconditionally, so the wrapping is
  // required even though the result is unused.
  if (isApexLanding(window.location.hostname, window.location.pathname)) {
    return (
      <QueryClientProvider client={queryClient}>
        <TenantProvider tenantSlug={APEX_TENANT_SLUG}>
          <ApexLandingPage />
        </TenantProvider>
      </QueryClientProvider>
    );
  }

  // FR-UI-OPS-1 AC-1: the operator surface is a separate route
  // surface under the same single SPA bundle, mounted only on
  // the platform apex domain. The check happens BEFORE
  // <TenantProvider> so the operator path never falls into the
  // OIDC bootstrap. The operator module provides its own
  // QueryClient (so the operator cache is isolated from the
  // tenant-side cache), its own token gate (no OIDC, no JWT),
  // and its own dispatch on /operator/*.
  //
  // ISS-f4fa1b7b-297: OperatorRoot internally wraps itself in
  // <QueryClientProvider client={queryClient}> (apps/web/src/operator/
  // OperatorRoot.tsx:88). No outer wrapping needed; double-wrap
  // would create nested cache scopes that disagree on staleness.
  if (isOperatorSurface(window.location.hostname, window.location.pathname)) {
    return <OperatorRoot />;
  }

  const tenantSlug = tenantSlugFromHostname(window.location.hostname);

  return (
    <QueryClientProvider client={queryClient}>
      <TenantProvider tenantSlug={tenantSlug}>
        <AppBootstrap tenantSlug={tenantSlug}>
          <SilentRefreshInstaller>
            <AuthStateRouter tenantSlug={tenantSlug} />
          </SilentRefreshInstaller>
        </AppBootstrap>
        <AdminRoute />
      </TenantProvider>
    </QueryClientProvider>
  );
}

// Re-export LoginRoute for explicit sign-in actions (e.g. sign-in
// button on the BootstrapErrorScreens). Not rendered by default —
// the AuthGuard kicks the OIDC flow on first unauthenticated render.
export { LoginRoute };
